
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'reference',
  source: 'endpoint',
  description: 'API reference for Plaid OAuth endpoints',
  childTocLevel: 4
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const Schema = makeShortcode("Schema");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='OAuth' subtitle='API reference for Plaid OAuth endpoints' compact mdxType="Header" />
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Endpoints`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/oauth/token`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Create or refresh an OAuth access token`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/oauth/introspect`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get metadata about an OAuth token`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/oauth/revoke`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Revoke an OAuth token`}</td>
        </tr>
      </tbody>
    </table>
    <Callout warning mdxType="Callout">
      <p>{`These endpoints are for customers, partners and services that are integrating with Plaid's OAuth service to obtain a token for sharing consumer reports or accessing the Plaid Dashboard or other Plaid services. They are not used for the Plaid Link flow where end users connect their financial institution accounts to Plaid using a bank's OAuth service. If you are a Plaid customer trying to ensure your app supports OAuth-based bank connections, see the `}<a parentName="p" {...{
          "href": "/docs/link/oauth"
        }}>{`OAuth Guide`}</a>{` instead.`}</p>
    </Callout>
    <h3 {...{
      "id": "endpoints"
    }}>{`Endpoints`}</h3>
    <h4 {...{
      "id": "oauthtoken"
    }}>{`/oauth/token`}</h4>
    <Schema method='POST' route='/oauth/token' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/oauth/token' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl 'https://sandbox.plaid.com/oauth/token' \\
--header 'Content-Type: application/json' \\
--data '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "grant_type": "urn:ietf:params:oauth:grant-type:token-exchange",
  "scope": "user:read",
  "subject_token_type":"urn:plaid:params:credit:multi-user",
  "audience": "urn:plaid:params:cra-partner:fannie-mae",
  "subject_token": "usr_9nSp2KuZ2x4JDw"
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request = {
    grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
    scope: 'user:read',
    subject_token_type: 'urn:plaid:params:credit:multi-user',
    audience: 'urn:plaid:params:cra-partner:fannie-mae',
    subject_token: userId
};

try {
    const response = await client.oauthToken(request);
} catch (error) {
    ...
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = OAuthTokenRequest(
    grant_type=OAuthGrantType("urn:ietf:params:oauth:grant-type:token-exchange"),
    scope="user:read",
    subject_token_type=OAuthSubjectTokenType("urn:plaid:params:credit:multi-user"),
    audience="urn:plaid:params:cra-partner:fannie-mae",
    subject_token=user_id
)

response = client.oauth_token(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`OAuthTokenRequest request = new OAuthTokenRequest()
    .grantType(OAuthGrantType.URN_IETF_PARAMS_OAUTH_GRANT_TYPE_TOKEN_EXCHANGE)
    .scope("user:read")
    .subjectTokenType(OAuthSubjectTokenType.CREDIT_MULTI_USER)
    .audience("urn:plaid:params:cra-partner:fannie-mae")
    .subjectToken(userId);
Response<OAuthTokenResponse> response = plaidClient.oauthToken(request).execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::OAuthTokenRequest.new(
  grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
  scope: 'user:read',
  subject_token_type: 'urn:plaid:params:credit:multi-user',
  audience: 'urn:plaid:params:cra-partner:fannie-mae',
  subject_token: user_id
)
response = client.oauth_token(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewOAuthTokenRequest(
    "urn:ietf:params:oauth:grant-type:token-exchange",
)
request.SetScope("user:read")
request.SetSubjectTokenType("urn:plaid:params:credit:multi-user")
request.SetAudience("urn:plaid:params:cra-partner:fannie-mae")
request.SetSubjectToken(user_id)

ctx := context.Background()
response, _, err := client.PlaidApi.OauthToken(ctx).OAuthTokenRequest(*request).Execute()
if err != nil {
    // Handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.OAuthTokenRequest{GrantType: "urn:ietf:params:oauth:grant-type:token-exchange"}
request.Scope = plaid.String("user:read")
request.SubjectTokenType = plaid.OAuthSubjectTokenType("urn:plaid:params:credit:multi-user").Ptr()
request.Audience = plaid.String("urn:plaid:params:cra-partner:fannie-mae")
request.SubjectToken = &user_id

ctx := context.Background()
response, err := client.OAuth.Token(ctx, request)
if err != nil {
    // Handle error
}
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/oauth/token' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "oauthintrospect"
    }}>{`/oauth/introspect`}</h4>
    <Schema method='POST' route='/oauth/introspect' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/oauth/introspect' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl 'https://sandbox.plaid.com/oauth/introspect' \\
--header 'Content-Type: application/json' \\
--data '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "token": "pda-RDdg0TUCB0FB25_UPIlnhA=="
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request = {
  token: accessToken
};

try {
  const response = await client.oauthIntrospect(request);
} catch (error) {
    ...
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = OAuthIntrospectRequest(
  token=access_token
)
response = client.oauth_introspect(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`OAuthIntrospectRequest request = new OAuthIntrospectRequest()
    .token(accessToken);
Response<OAuthIntrospectResponse> response = plaidClient.oauthIntrospect(request).execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::OAuthIntrospectRequest.new(
  "token": access_token
)
response = client.oauth_introspect(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewOAuthIntrospectRequest(
    accessToken,
)
ctx := context.Background()
response, _, err := client.PlaidApi.OauthIntrospect(ctx).OAuthIntrospectRequest(*request).Execute()
if err != nil {
    // Handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.OAuthIntrospectRequest{Token: accessToken}
ctx := context.Background()
response, err := client.OAuth.Introspect(ctx, request)
if err != nil {
    // Handle error
}
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/oauth/introspect' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "oauthrevoke"
    }}>{`/oauth/revoke`}</h4>
    <Schema method='POST' route='/oauth/revoke' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/oauth/revoke' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/oauth/revoke \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "token": "pda-RDdg0TUCB0FB25_UPIlnhA=="
  }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request = {
  token: accessToken
};

try {
  const response = await client.oauthRevoke(request);
} catch (error) {
    ...
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = OAuthRevokeRequest(
  token=access_token
)
response = client.oauth_revoke(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`OAuthRevokeRequest request = new OAuthRevokeRequest()
    .token(accessToken);
Response<OAuthRevokeResponse> response = plaidClient.oauthRevoke(request).execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::OAuthRevokeRequest.new(
  "token": access_token
)
response = client.oauth_revoke(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewOAuthRevokeRequest(
    accessToken,
)
ctx := context.Background()
response, _, err := client.PlaidApi.OauthRevoke(ctx).OAuthRevokeRequest(*request).Execute()
if err != nil {
    // Handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.OAuthRevokeRequest{Token: accessToken}
ctx := context.Background()
response, err := client.OAuth.Revoke(ctx, request)
if err != nil {
    // Handle error
}
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/oauth/revoke' type='response' mdxType="Schema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "endpoints",
        level: 3,
        title: "Endpoints",
      },
{
        id: "oauthtoken",
        level: 4,
        title: "/oauth/token",
      },
{
        id: "oauthintrospect",
        level: 4,
        title: "/oauth/introspect",
      },
{
        id: "oauthrevoke",
        level: 4,
        title: "/oauth/revoke",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
